import { useEffect, useRef, useState } from 'react';
import { get, patch, post, qs } from '../lib/api';
import { navigate, storageGet, storageSet, useDebounced, useLoad } from '../lib/hooks';
import { BulkEditSheet, PickAll, type BulkField, type BulkValues } from '../ui/bulk';
import { useCart, type CartCustomer } from '../lib/cart';
import { useSession } from '../lib/session';
import { usePlan, type Industry } from '../lib/plan';
import { formatCents } from '../../shared/money';
import { formatDateTime, formatPhone, zone } from '../../shared/format';
import { ORDER_STATUS_LABELS, SHIP_CARRIER_LABELS, type OrderStatus } from '../../shared/orders';
import { PRICE_SOURCE_LABELS, SUPPLIERS, customerPartName, extractTracking, formatShortDate, supplierByName, type OrderPayPlan, type TrackStatus } from '../../shared/parts';
import { Badge, Button, Confirm, Cost, Empty, Field, Money, MoneyInput, Profit, Sheet, Spinner, useAction, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { usePrint } from '../ui/print';
import { TASK_ART } from '../ui/art';
import { PhotoTile } from '../ui/photo';
import { TASK_PHOTOS } from '../../shared/photos';
import { CustomerSearch } from './CustomerPicker';
import { PartSearchPanel, type PickedPart } from './PartSearch';
import { printOrder } from './OrderPrint';
import { useCloudPrint } from '../lib/printing';

const TONE: Record<OrderStatus, 'blue' | 'amber' | 'green' | 'plain' | 'red'> = { ordered: 'amber', shipped: 'blue', arrived: 'green', picked_up: 'plain', cancelled: 'red' };
const TRACK_TONE: Record<TrackStatus, 'blue' | 'amber' | 'green' | 'plain' | 'red'> = {
  pre_transit: 'plain', in_transit: 'blue', out_for_delivery: 'blue', delivered: 'green', pickup_ready: 'amber', exception: 'red', unknown: 'plain',
};
/** The list's filters; "Phones here" (the item a customer left with us) names the shop's own item. */
const filtersFor = (item: string) => [
  { id: 'open', label: 'All open' }, { id: 'phone', label: `${item[0].toUpperCase() + item.slice(1)}s here` }, { id: 'unpaid', label: 'Not paid' },
  { id: 'ordered', label: 'Ordered' }, { id: 'shipped', label: 'On the way' }, { id: 'arrived', label: 'Arrived' }, { id: 'done', label: 'Done' },
];
const PART_KEY = 'sos_order_part';
const day = (ms: number) => new Intl.DateTimeFormat('en-US', { timeZone: zone.current, month: 'short', day: 'numeric' }).format(new Date(ms));
const carrierName = (c: string | null) => SHIP_CARRIER_LABELS[(c ?? 'other') as keyof typeof SHIP_CARRIER_LABELS] ?? '';

/** Picked from the top search bar's "Order a part": opens a new order with that part filled in. */
export function startOrderWithPart(p: PickedPart) {
  storageSet(PART_KEY, p);
  navigate(`/orders/new?part=${Date.now()}`);
}

function PaidBadge({ o }: { o: { paid: unknown; price_cents: number; open?: boolean; status: string } }) {
  if (o.paid) return <Badge tone="green" icon="check">Paid</Badge>;
  if (o.price_cents > 0 && o.status !== 'cancelled') return <Badge tone="amber">Not paid</Badge>;
  return null;
}

export function OrdersScreen({ query }: { query: URLSearchParams }) {
  const item = leftItem(usePlan().industry).item;
  const here = `${item[0].toUpperCase() + item.slice(1)} here`;
  const [status, setStatus] = useState(query.get('status') ?? 'open');
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const list = useLoad<any>(() => get(`/api/orders${qs({ status, q: dq })}`), [status, dq]);
  // Tick orders (or Select all) and change their status, supplier, ETA or tracking in one go.
  const [picked, setPicked] = useState<string[]>([]);
  const [editingMany, setEditingMany] = useState(false);
  const rows: any[] = list.data?.orders ?? [];
  const chosen = rows.filter((o) => picked.includes(o.id));
  const allPicked = rows.length > 0 && chosen.length === rows.length;
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const bulkFields: BulkField[] = [
    { key: 'status', label: 'Status', kind: 'select', options: (['ordered', 'shipped', 'arrived', 'picked_up'] as OrderStatus[]).map((st) => ({ value: st, label: ORDER_STATUS_LABELS[st] })), hint: 'Arrived texts the customers who agreed to texts' },
    { key: 'supplier', label: 'Supplier', kind: 'text', maxLength: 120 },
    { key: 'eta', label: 'Expected on', kind: 'date' },
    { key: 'notifySms', label: 'Text updates', kind: 'yesno', yes: 'On', no: 'Off' },
  ];
  const applyBulk = async (it: { id: string }, v: BulkValues) => {
    const body: Record<string, unknown> = {};
    if (typeof v.supplier === 'string') body.supplier = v.supplier.trim() || null;
    if (typeof v.eta === 'string') body.eta = v.eta;
    if (typeof v.notifySms === 'boolean') body.notifySms = v.notifySms;
    if (Object.keys(body).length) await patch(`/api/orders/${it.id}`, body);
    if (typeof v.status === 'string') await post(`/api/orders/${it.id}/status`, { status: v.status });
  };
  return (
    <div className="page">
      <div className="page-head">
        <h1>Special orders</h1>
        <Button variant="primary" icon="plus" onClick={() => navigate('/orders/new')}>New order</Button>
      </div>
      <div className="chips">
        {filtersFor(item).map((f) => (
          <button key={f.id} type="button" className="chip" aria-pressed={status === f.id} onClick={() => setStatus(f.id)}>
            {f.label}{list.data?.counts?.[f.id] ? ` (${list.data.counts[f.id]})` : ''}
          </button>
        ))}
      </div>
      <div className="search"><Icon.search /><input className="input input-lg" placeholder="O-1001, tracking number, item, SKU or customer" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search orders" /></div>
      {rows.length ? (
        <div className="pick-bar" role="region" aria-label="Pick orders">
          <PickAll all={allPicked} picked={chosen.length} total={rows.length} what="orders" onToggle={() => setPicked(allPicked ? [] : rows.map((o) => o.id))} />
          <span className="grow" />
          {chosen.length ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setPicked([])}>Clear</Button>
              <Button size="sm" icon="edit" onClick={() => setEditingMany(true)}>Edit {chosen.length}</Button>
            </>
          ) : <span className="small muted">Tick orders to change their status, supplier, expected date or texting together</span>}
        </div>
      ) : null}
      {!list.data ? <Spinner /> : list.data.orders.length ? (
        <div className="list">
          {list.data.orders.map((o: any) => {
            const open = ['ordered', 'shipped', 'arrived'].includes(o.status);
            const showEta = open && o.status !== 'arrived' && o.track.expected && o.track.status !== 'delivered';
            return (
              <div key={o.id} className="pc-row">
              <label className="pc-pick" aria-label={`Pick ${o.number}`}><input type="checkbox" checked={picked.includes(o.id)} onChange={() => toggle(o.id)} /></label>
              <a className="list-row order-row" href={`#/orders/${o.id}`}>
                <span className="thumb">{o.part_image ? <img className="pic" src={o.part_image} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <Icon.truck />}</span>
                <span className="main-col">
                  <span className="t" style={{ display: 'block' }}>{o.number} · {o.description}</span>
                  <span className="s" style={{ display: 'block' }}>
                    {o.customer_name} · ordered {day(o.created_at)}{o.supplier ? ` · ${o.supplier}` : ''}{o.tracking ? ` · ${carrierName(o.carrier)} ${o.tracking}` : ''}
                  </span>
                  <span className="order-flags">
                    <Badge tone={TONE[o.status as OrderStatus]}>{o.status_label}</Badge>
                    {open && o.track.status ? <Badge tone={TRACK_TONE[o.track.status as TrackStatus]}>{o.track.status_label}</Badge> : null}
                    {showEta ? <span className="small strong">Expected {formatShortDate(o.track.expected)}</span> : null}
                    {open && o.device_left ? <Badge tone="blue">{here}</Badge> : null}
                    <PaidBadge o={o} />
                  </span>
                </span>
                <span className="pill-row" style={{ justifyContent: 'flex-end' }}>
                  <Money cents={o.price_cents} className="strong" />
                  {o.profit_cents !== null && o.profit_cents !== undefined ? <Profit cents={o.profit_cents} /> : null}
                </span>
              </a>
              </div>
            );
          })}
        </div>
      ) : <Empty icon="truck" title="No orders here." />}
      {editingMany ? <BulkEditSheet title="Edit orders" what="orders" items={chosen.map((o) => ({ id: o.id, label: `${o.number} · ${o.description} · ${o.customer_name}` }))} fields={bulkFields} apply={applyBulk} onClose={() => setEditingMany(false)} onDone={() => { setEditingMany(false); setPicked([]); list.reload(); }} /> : null}
    </div>
  );
}

/** Two big answers to one question (paid now or later; phone left or kept). */
function Choice<T extends string>({ label, value, options, onChange }: { label: string; value: T | null; options: { id: T; title: string; sub: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="field full">
      <span>{label}</span>
      <div className="choice-row" role="group" aria-label={label}>
        {options.map((o) => (
          <button key={o.id} type="button" className="choice" aria-pressed={value === o.id} onClick={() => onChange(o.id)}>
            <span className="t">{o.title}</span>
            <span className="s">{o.sub}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const PAY_OPTIONS: { id: OrderPayPlan; title: string; sub: string }[] = [
  { id: 'now', title: 'Paying now', sub: 'Take payment right after saving' },
  { id: 'pickup', title: 'Pays at pickup', sub: 'Pays when the part comes in' },
];
/** What the customer may leave while the part is on its way, by kind of shop. */
function leftItem(industry: Industry): { item: string; example: string; part: string } {
  if (industry === 'auto') return { item: 'vehicle', example: 'e.g. 2019 Mazda3, gray', part: 'e.g. front brake pads for a 2019 Mazda3, or the part number' };
  if (industry === 'computer') return { item: 'computer', example: 'e.g. MacBook Air 13-inch, silver', part: 'e.g. MacBook Air battery, or the part number' };
  if (industry === 'vacuum') return { item: 'vacuum', example: 'e.g. Dyson V11, purple', part: 'e.g. Dyson V11 battery, or the part number' };
  return { item: 'phone', example: 'e.g. iPhone 13, blue', part: 'e.g. iPhone 13 screen, Galaxy S25 Ultra back glass, 107082080901' };
}
const leftOptions = (item: string): { id: 'left' | 'kept'; title: string; sub: string }[] => [
  { id: 'left', title: `Left the ${item} with us`, sub: item === 'phone' ? 'Print a sticker for the back of it' : 'Print a sticker to tag it' },
  { id: 'kept', title: `Kept their ${item}`, sub: 'They bring it back when the part is in' },
];

/** Today in the store's time zone, as YYYY-MM-DD. */
const todayIso = (tz?: string | null) => {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz || zone.current }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA', { timeZone: zone.current }).format(new Date());
  }
};
const plusDays = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n, 12)).toISOString().slice(0, 10);
};
const ARRIVAL_PICKS = [{ days: 1, label: 'Tomorrow' }, { days: 2, label: 'In 2 days' }, { days: 3, label: 'In 3 days' }, { days: 7, label: 'In a week' }];

/**
 * When the part comes in: a quick pick, a day from the calendar, or "not sure yet". The day
 * prints big on the customer's receipt; tracking (or the PHORA order) updates it later.
 */
function ArrivalPick({ value, onChange }: { value: string | null; onChange: (v: string) => void }) {
  const { me } = useSession();
  // Live tracking (EasyPost, PHORA orders) is West Coast Wireless's store only, for now.
  const own = usePlan().home;
  const today = todayIso(me?.settings?.timezone);
  const picks = ARRIVAL_PICKS.map((x) => ({ ...x, date: plusDays(today, x.days) }));
  const other = !!value && !picks.some((x) => x.date === value);
  return (
    <div className="field full">
      <span>When will it arrive? <em className="hint" style={{ fontStyle: 'normal' }}>{own ? 'prints big on the customer’s receipt; tracking updates it' : 'prints big on the customer’s receipt'}</em></span>
      <div className="chips" role="group" aria-label="When will it arrive?">
        {picks.map((x) => (
          <button key={x.days} type="button" className="chip" aria-pressed={value === x.date} onClick={() => onChange(x.date)}>
            {x.label} <span className="pc-price">{formatShortDate(x.date)}</span>
          </button>
        ))}
        <input
          className={`input arrival-date${other ? ' on' : ''}`} type="date" min={today} value={other ? value! : ''} aria-label="Arrival date"
          onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
        />
        <button type="button" className="chip" aria-pressed={value === ''} onClick={() => onChange('')}>Not sure yet</button>
      </div>
    </div>
  );
}

/** What was found in the tracking box: "UPS 1Z…", from a number or a whole pasted email. */
function TrackingHint({ text }: { text: string }) {
  if (!text.trim()) return null;
  const t = extractTracking(text);
  if (!t.tracking) return <div className="small" style={{ color: 'var(--amber)' }}>No tracking number found yet.</div>;
  return <div className="small muted">Found {t.carrier && t.carrier !== 'other' ? `${carrierName(t.carrier)} ` : ''}<span className="strong">{t.tracking}</span>{t.orderRef ? ` · order #${t.orderRef}` : ''}</div>;
}

function PickedCard({ part, onChange, onRemove }: { part: PickedPart; onChange: () => void; onRemove: () => void }) {
  return (
    <div className="picked">
      <span className="part-thumb" aria-hidden>{part.image ? <img src={part.image} alt="" referrerPolicy="no-referrer" /> : <Icon.tag />}</span>
      <div className="grow">
        <div className="strong">{part.name}</div>
        <div className="small muted">
          {part.supplier_name}{part.sku ? ` · SKU ${part.sku}` : ''} · {part.in_stock === false ? 'Out of stock at the supplier' : part.in_stock ? 'In stock at the supplier' : 'Stock unknown'}
          {part.url ? <> · <a href={part.url} target="_blank" rel="noreferrer noopener">Open on their site</a></> : null}
        </div>
      </div>
      <Button size="sm" onClick={onChange}>Change</Button>
      <Button size="sm" variant="ghost" onClick={onRemove}>Remove</Button>
    </div>
  );
}

export function OrderNewScreen({ query }: { query: URLSearchParams }) {
  const cart = useCart();
  const toast = useToast();
  const { can } = useSession();
  const { run, busy } = useAction();
  const [customer, setCustomer] = useState<CartCustomer | null>(null);
  const [desc, setDesc] = useState('');
  const [searching, setSearching] = useState(false);
  const [part, setPart] = useState<PickedPart | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  // What the part costs the store: the owner types it (or leaves it blank for the supplier's price from the search).
  const seesCost = can('inventory.view_cost');
  const [cost, setCost] = useState<number | null>(null);
  const [supplier, setSupplier] = useState('');
  const [supplierOther, setSupplierOther] = useState('');
  const [f, setF] = useState({ supplierRef: '', tracking: '', note: '', device: '' });
  // When it arrives: null until it's answered, '' for "not sure yet", or the day (YYYY-MM-DD).
  const [eta, setEta] = useState<string | null>(null);
  const [payPlan, setPayPlan] = useState<OrderPayPlan | null>(null);
  const [phone, setPhone] = useState<'left' | 'kept' | null>(null);
  // The texting box: unticked until the customer says yes (a yes already on their record is kept).
  const [notify, setNotify] = useState(false);
  const [texting, setTexting] = useState<{ agreed: boolean; opted_out: boolean; text: string } | null>(null);
  useEffect(() => {
    if (!customer?.id || !customer.phone) { setTexting(null); setNotify(false); return; }
    let live = true;
    get(`/api/customers/${customer.id}/texting`).then((t) => { if (live) { setTexting(t); setNotify(!!t.agreed && !t.opted_out); } }).catch(() => undefined);
    return () => { live = false; };
  }, [customer?.id, customer?.phone]);
  const descRef = useRef<HTMLInputElement>(null);
  // The suppliers' parts search, PHORA order tracking and texting run on West Coast Wireless's own
  // accounts: its store only, for now. Every other shop types the part and the supplier in.
  const plan = usePlan();
  const own = plan.home;
  const words = leftItem(plan.industry);

  const pick = (p: PickedPart) => {
    setPart(p);
    setDesc(customerPartName(p.name));
    setSupplier(p.supplier_name);
    if (p.price_cents) setPrice(p.price_cents);
    setSearching(false);
  };
  useEffect(() => {
    const id = query.get('customer');
    if (id) get(`/api/customers/${id}`).then((c) => setCustomer({ id: c.id, name: c.name, phone: c.phone, store_credit_cents: c.store_credit_cents })).catch(() => undefined);
    if (query.get('part')) {
      const p = storageGet<PickedPart | null>(PART_KEY, null);
      storageSet(PART_KEY, null);
      if (p?.name) pick(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const supplierValue = !own || supplier === 'other' ? supplierOther.trim() : supplier;
  const isPhora = own && supplierByName(supplierValue)?.id === 'phora';
  const missing = !customer ? 'Pick the customer' : !desc.trim() ? 'Say what you’re ordering' : price === null ? 'Enter the customer’s price' : eta === null ? 'Answer: when will it arrive?' : !payPlan ? 'Answer: paying now or at pickup?' : !phone ? `Answer: did they leave the ${words.item}?` : null;
  const save = async () => {
    if (!customer || !payPlan || !phone) return;
    const r = await run(() => post('/api/orders', {
      customerId: customer.id, description: desc.trim(), priceCents: price ?? 0, supplier: supplierValue || null, supplierRef: f.supplierRef.trim() || null,
      ...(seesCost && cost !== null ? { costCents: cost } : {}),
      supplierSku: part?.sku ?? null, partUrl: part?.url ?? null, partImage: part?.image ?? null, tracking: f.tracking.trim() || null, eta: eta || null,
      note: f.note.trim() || null, notifySms: own && notify, payPlan, deviceLeft: phone === 'left', device: phone === 'left' ? f.device.trim() || null : null,
    }), { success: 'Order saved.' });
    if (!r) return;
    if (payPlan === 'now' && r.price_cents > 0) {
      cart.addOrder({ id: r.id, number: r.number, description: r.description, price_cents: r.price_cents, taxable: r.taxable });
      if (!cart.cart.customer) {
        const c = await get(`/api/customers/${customer.id}`).catch(() => null);
        if (c) cart.setCustomer({ id: c.id, name: c.name, phone: c.phone, email: c.email, store_credit_cents: c.store_credit_cents, points_balance: c.points_balance, member_code: c.member_code });
      }
      toast(`Take payment for ${r.number}. Its receipt and phone sticker print from the finished sale.`, 'info');
      navigate('/sale');
    } else {
      navigate(`/orders/${r.id}?print=1`);
    }
  };
  const Art = TASK_ART.order;
  const priceHint = part?.price_source && price === part.price_cents ? `${PRICE_SOURCE_LABELS[part.price_source]} · taxed like any item` : 'taxed like any item';
  return (
    <div className="page">
      <div className="page-head">
        <PhotoTile src={TASK_PHOTOS.order} alt="" size={64} icon="truck" fallback={<Art size={56} />} />
        <h1 style={{ marginRight: 'auto' }}>New special order</h1>
        <Button variant="ghost" icon="list" onClick={() => navigate('/orders')}>All orders</Button>
      </div>
      <section className="card stack">
        <CustomerSearch value={customer} onChange={setCustomer} />
        <div className="form-grid">
          <Field label="What you’re ordering" hint={own ? 'search the suppliers by phone, part or SKU' : 'the part, with its part number if you have it'} className="full">
            <div className="search">
              <Icon.search />
              <input
                ref={descRef}
                className="input input-lg"
                value={desc}
                onChange={(e) => { setDesc(e.target.value); if (!part && own) setSearching(true); }}
                onFocus={() => { if (!part && own) setSearching(true); }}
                onKeyDown={(e) => { if (e.key === 'Escape') setSearching(false); }}
                placeholder={words.part}
                aria-label="What you’re ordering"
                autoComplete="off"
              />
            </div>
          </Field>
          {searching ? <div className="full"><PartSearchPanel query={desc} onPick={pick} onClose={() => setSearching(false)} /></div> : null}
          {part && !searching ? <div className="full"><PickedCard part={part} onChange={() => { setSearching(true); descRef.current?.focus(); }} onRemove={() => setPart(null)} /></div> : null}
          <Field label="Customer’s price" hint={priceHint}><MoneyInput value={price} onChange={setPrice} ariaLabel="Customer’s price" /></Field>
          {seesCost ? (
            <Field label="Your cost" hint={cost !== null && price !== null ? <span className="pill-row"><Cost cents={cost} /><Profit cents={price - cost} /></span> : 'optional · blank takes the supplier’s price from the search'}>
              <MoneyInput value={cost} onChange={setCost} ariaLabel="Your cost" />
            </Field>
          ) : null}
          <Field label="Supplier" hint={own ? undefined : 'optional'}>
            {own ? (
              <select className="select" value={supplier} onChange={(e) => setSupplier(e.target.value)} aria-label="Supplier">
                <option value="">Pick the supplier</option>
                {SUPPLIERS.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                <option value="other">Other…</option>
              </select>
            ) : <input className="input" value={supplierOther} onChange={(e) => setSupplierOther(e.target.value)} placeholder="Who you’re ordering from" aria-label="Supplier" />}
          </Field>
          {own && supplier === 'other' ? <Field label="Other supplier"><input className="input" value={supplierOther} onChange={(e) => setSupplierOther(e.target.value)} placeholder="Supplier name" /></Field> : null}
          <Field label={isPhora ? 'PHORA order #' : 'Supplier order #'} hint={isPhora ? 'tracking fills in by itself' : 'optional'}>
            <input className="input" value={f.supplierRef} onChange={set('supplierRef')} aria-label="Supplier order number" />
          </Field>
          <Field label="Tracking number" hint="paste the number or the whole shipping email" className="full">
            <textarea className="input" rows={2} value={f.tracking} onChange={set('tracking')} aria-label="Tracking number" placeholder="add it now or later" />
            <TrackingHint text={f.tracking} />
          </Field>
          <ArrivalPick value={eta} onChange={setEta} />
          <Choice label="Payment" value={payPlan} options={PAY_OPTIONS} onChange={setPayPlan} />
          <Choice label={`The customer’s ${words.item}`} value={phone} options={leftOptions(words.item)} onChange={setPhone} />
          {phone === 'left' ? <Field label={`Which ${words.item}`} hint="model and color, so it's easy to find" className="full"><input className="input" value={f.device} onChange={set('device')} placeholder={words.example} aria-label={`Which ${words.item}`} /></Field> : null}
          <Field label="Note" className="full"><input className="input" value={f.note} onChange={set('note')} aria-label="Note" /></Field>
        </div>
        {own ? (texting?.opted_out ? (
          <div className="banner warn"><Icon.alert /><div className="small">{customer?.name} replied STOP to our texts. To get texts again they text START to the store’s number.</div></div>
        ) : (
          <label className="consent-box">
            <input type="checkbox" checked={notify} disabled={!customer?.phone} onChange={(e) => setNotify(e.target.checked)} aria-label="Customer agrees to a text when the order arrives" />
            <span className="small"><strong>Ask: “Can we text you when your order arrives?”</strong>{customer && !customer.phone ? ' (no phone on file)' : ''}<br /><span className="muted">{texting?.text ?? 'Optional. Msg & data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help.'}</span></span>
          </label>
        )) : null}
        <div className="row-wrap">
          <Button variant="primary" size="lg" busy={busy} disabled={!!missing} onClick={save}>{payPlan === 'now' && (price ?? 0) > 0 ? 'Save and take payment' : 'Save order'}</Button>
          {missing ? <span className="small muted">{missing}</span> : null}
        </div>
      </section>
    </div>
  );
}

/** Print buttons for a special order (its screen, and the finished sale that paid for it). */
export function OrderPrintButtons({ orderId, deviceLeft, compact }: { orderId: string; deviceLeft?: boolean; compact?: boolean }) {
  const print = usePrint();
  const cloud = useCloudPrint();
  const toast = useToast();
  const go = (which: 'receipt' | 'sticker') => printOrder(print, orderId, which, cloud.printOut).catch((e) => toast(e.message, 'error'));
  return (
    <>
      <Button icon="printer" size={compact ? 'sm' : 'md'} onClick={() => go('receipt')}>Customer receipt</Button>
      <Button icon="tag" size={compact ? 'sm' : 'md'} variant={deviceLeft ? 'secondary' : 'ghost'} onClick={() => go('sticker')}>Phone sticker</Button>
    </>
  );
}

/** On a finished sale: print the order receipt and sticker for each special order it paid for. */
export function SaleOrderPrints({ lines }: { lines: { order_id?: string | null; description: string }[] }) {
  const orders = lines.filter((l) => l.order_id);
  if (!orders.length) return null;
  return (
    <div className="card card-tight stack-sm" style={{ textAlign: 'left' }}>
      {orders.map((l) => (
        <div key={l.order_id} className="row-wrap">
          <span className="grow strong">{l.description}</span>
          <OrderPrintButtons orderId={l.order_id!} compact />
          <a className="btn btn-ghost btn-sm" href={`#/orders/${l.order_id}`}>Open order</a>
        </div>
      ))}
    </div>
  );
}

function PhoneSheet({ order, onClose, onSaved }: { order: any; onClose: () => void; onSaved: (o: any) => void }) {
  const { run, busy } = useAction();
  const words = leftItem(usePlan().industry);
  const [left, setLeft] = useState<'left' | 'kept'>(order.device_left ? 'left' : 'kept');
  const [device, setDevice] = useState(order.device ?? '');
  const save = async () => {
    const r = await run(() => patch(`/api/orders/${order.id}`, { deviceLeft: left === 'left', device: left === 'left' ? device.trim() || null : null }), { success: 'Saved.' });
    if (r) onSaved(r);
  };
  return (
    <Sheet title={`The customer’s ${words.item}`} onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} onClick={save}>Save</Button></>}>
      <div className="form-grid">
        <Choice label={`Where is the ${words.item}?`} value={left} options={[{ id: 'left', title: 'Here with us', sub: 'Waiting for the part' }, { id: 'kept', title: 'With the customer', sub: 'Not in the store' }]} onChange={setLeft} />
        {left === 'left' ? <Field label={`Which ${words.item}`} className="full"><input className="input" value={device} onChange={(e) => setDevice(e.target.value)} placeholder={words.example} /></Field> : null}
      </div>
    </Sheet>
  );
}

function TrackingCard({ x, onChange }: { x: any; onChange: (o: any) => void }) {
  const toast = useToast();
  const { run, busy } = useAction();
  const setup = useLoad<any>(() => get('/api/tracking/setup'), []);
  const [text, setText] = useState('');
  const [ref, setRef] = useState(x.supplier_ref ?? '');
  const [eta, setEta] = useState(x.eta ?? '');
  const t = x.track;
  const canTrack = x.open && x.status !== 'arrived';
  const own = usePlan().home;
  const isPhora = own && x.supplier_id === 'phora';
  const live = !!setup.data && (setup.data.live_tracking || (isPhora && !!setup.data.phora));
  const saveTracking = async () => {
    const r = await run(() => patch(`/api/orders/${x.id}`, { tracking: text }), { success: 'Tracking saved.' });
    if (r) { onChange(r); setText(''); }
  };
  const check = async () => {
    const r = await run(() => post(`/api/orders/${x.id}/track`));
    if (!r) return;
    onChange(r);
    if (r.check?.error) toast(r.check.error, 'info');
    else if (r.check?.checked) toast(r.check.changed ? 'Tracking updated.' : 'No change since the last check.', 'good');
  };
  return (
    <section className="card stack-sm">
      <div className="spread"><h3>Tracking</h3>{t.status ? <Badge tone={TRACK_TONE[t.status as TrackStatus]}>{t.status_label}</Badge> : null}</div>
      {x.tracking ? (
        <div className="stack-sm">
          <div className="row-wrap">
            <span className="strong">{carrierName(x.carrier)} {x.tracking}</span>
            {t.link ? <a className="btn btn-secondary btn-sm" href={t.link} target="_blank" rel="noreferrer noopener"><Icon.truck />Track it</a> : null}
          </div>
          {t.status === 'delivered' && t.delivered_at ? <div className="banner"><Icon.check /><div><div className="strong">Delivered {formatDateTime(t.delivered_at)}</div><div className="small">{own ? 'Check it in, then tap “It arrived” to text the customer.' : 'Check it in, then tap “It arrived”.'}</div></div></div> : null}
          {t.expected && t.status !== 'delivered' && x.status !== 'arrived' ? (
            <div className="eta-box"><span className="small muted">Expected</span><span className="eta">{formatShortDate(t.expected)}</span><span className="tiny muted">{t.expected_from === 'carrier' ? `from ${t.source === 'phora' ? 'the PHORA order' : carrierName(x.carrier) || 'the carrier'}` : 'typed in'}</span></div>
          ) : null}
          {t.detail ? <div className="small">{t.detail}</div> : null}
          {t.checked_at ? <div className="tiny muted">Checked {formatDateTime(t.checked_at)}{t.source === 'easypost' ? ' · live tracking' : t.source === 'phora' ? ' · from the PHORA store' : ''}</div> : null}
          {t.error ? <div className="small" style={{ color: 'var(--amber)' }}>{t.error}</div> : null}
        </div>
      ) : <p className="small muted">{isPhora ? 'No tracking yet. It fills in from the PHORA order once it ships.' : 'No tracking number yet.'}</p>}
      {canTrack ? (
        <div className="track-edit">
          <label htmlFor="trk-in">{x.tracking ? 'Change tracking' : 'Tracking number'}</label>
          <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <textarea id="trk-in" className="input grow" rows={2} placeholder="Tracking number or the shipping email" value={text} onChange={(e) => setText(e.target.value)} aria-label="Tracking number" />
            <Button busy={busy} disabled={!text.trim()} onClick={saveTracking}>Save</Button>
          </div>
          <TrackingHint text={text} />
          {isPhora ? (
            <>
              <label htmlFor="phora-ref">PHORA order #</label>
              <div className="row" style={{ gap: 8 }}>
                <input id="phora-ref" className="input grow" placeholder="e.g. 1042" value={ref} onChange={(e) => setRef(e.target.value)} aria-label="PHORA order number" />
                <Button busy={busy} disabled={ref.trim() === (x.supplier_ref ?? '')} onClick={async () => { const r = await run(() => patch(`/api/orders/${x.id}`, { supplierRef: ref.trim() || null }), { success: 'Saved.' }); if (r) onChange(r); }}>Save</Button>
              </div>
            </>
          ) : null}
          <label htmlFor="eta-in">Expected</label>
          <div className="row" style={{ gap: 8 }}>
            <input id="eta-in" className="input grow" type="date" value={eta} onChange={(e) => setEta(e.target.value)} />
            <Button busy={busy} disabled={eta === (x.eta ?? '')} onClick={async () => { const r = await run(() => patch(`/api/orders/${x.id}`, { eta: eta || null }), { success: 'Saved.' }); if (r) onChange(r); }}>Save</Button>
          </div>
          {live && (x.tracking || isPhora) ? <div><Button variant="ghost" icon="refresh" busy={busy} onClick={check}>Check tracking now</Button></div> : null}
          {setup.data && !live ? <p className="tiny muted">{own ? 'Live tracking is off, so the status and date don’t update by themselves. An owner can turn it on in Settings → Special orders.' : 'The status and date don’t update by themselves: change them here when the part moves.'}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

/** The owner's line on an order: what the part cost in red, what's left of the price in green, and a way to fix the cost. */
function OrderCostLine({ x, onChange }: { x: any; onChange: (o: any) => void }) {
  const { run, busy } = useAction();
  const [editing, setEditing] = useState(false);
  const [cost, setCost] = useState<number | null>(x.cost_cents);
  useEffect(() => setCost(x.cost_cents), [x.cost_cents]);
  const save = async () => {
    if (cost === null) return;
    const r = await run(() => patch(`/api/orders/${x.id}`, { costCents: cost }), { success: 'Cost saved.' });
    if (r) { onChange(r); setEditing(false); }
  };
  if (editing) {
    return (
      <span className="row-wrap" style={{ gap: 8 }}>
        <MoneyInput value={cost} onChange={setCost} ariaLabel="Your cost" />
        <Button size="sm" variant="primary" busy={busy} disabled={cost === null || cost === x.cost_cents} onClick={save}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setCost(x.cost_cents); }}>Cancel</Button>
      </span>
    );
  }
  return (
    <span className="pill-row">
      <Cost cents={x.cost_cents} />
      <Profit cents={x.profit_cents ?? x.price_cents - x.cost_cents} />
      {!x.paid ? <button type="button" className="link-btn" onClick={() => setEditing(true)}>Change cost</button> : null}
    </span>
  );
}

export function OrderDetailScreen({ id, query }: { id: string; query?: URLSearchParams }) {
  const cart = useCart();
  const toast = useToast();
  const print = usePrint();
  const cloud = useCloudPrint();
  const { run, busy } = useAction();
  const o = useLoad<any>(() => get(`/api/orders/${id}`), [id]);
  const [cancel, setCancel] = useState(false);
  const [reason, setReason] = useState('');
  const [phoneSheet, setPhoneSheet] = useState(false);
  const plan = usePlan();
  const words = leftItem(plan.industry);
  const printed = useRef(false);
  const x = o.data;
  useEffect(() => {
    if (x && query?.get('print') === '1' && !printed.current) {
      printed.current = true;
      history.replaceState(null, '', `#/orders/${id}`);
      printOrder(print, id, x.device_left ? 'both' : 'receipt', cloud.printOut).catch((e) => toast(e.message, 'error'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x]);
  if (!x) return <div className="page">{o.error ? <Empty icon="alert" title="This order couldn’t be loaded." /> : <Spinner />}</div>;
  const setStatus = async (status: OrderStatus, note?: string) => {
    const r = await run(() => post(`/api/orders/${id}/status`, { status, note: note ?? null }));
    if (r) {
      o.setData(r);
      if (r.text) toast(r.text.message, r.text.status === 'not_configured' ? 'info' : 'good');
    }
  };
  const checkout = async (prepay: boolean) => {
    if (prepay && x.pay_plan !== 'now') {
      const r = await run(() => patch(`/api/orders/${id}`, { payPlan: 'now' }));
      if (!r) return;
    }
    if (!cart.addOrder({ id: x.id, number: x.number, description: x.description, price_cents: x.price_cents, taxable: x.taxable })) toast('This order is already in the sale.', 'info');
    if (!cart.cart.customer) {
      const c = await get(`/api/customers/${x.customer.id}`).catch(() => null);
      if (c) cart.setCustomer({ id: c.id, name: c.name, phone: c.phone, email: c.email, store_credit_cents: c.store_credit_cents, points_balance: c.points_balance, member_code: c.member_code });
    }
    navigate('/sale');
  };
  const arrived = x.status === 'arrived';
  return (
    <div className="page">
      <div className="page-head">
        {x.part_image ? <span className="thumb" style={{ width: 64, height: 64 }}><img className="pic" src={x.part_image} alt="" referrerPolicy="no-referrer" style={{ width: 64, height: 64 }} /></span> : null}
        <div style={{ marginRight: 'auto', minWidth: 0 }}>
          <h1>{x.number} · {x.description}</h1>
          <p className="muted"><a href={`#/customers/${x.customer.id}`}>{x.customer.name}</a> · {formatPhone(x.customer.phone)} · ordered {formatDateTime(x.created_at)} by {x.created_by_name}</p>
        </div>
        <div className="row-wrap">
          <Badge tone={TONE[x.status as OrderStatus]}>{x.status_label}</Badge>
          <PaidBadge o={x} />
          {x.open && x.device_left ? <Badge tone="blue">{words.item[0].toUpperCase() + words.item.slice(1)} here</Badge> : null}
        </div>
      </div>
      <div className="row-wrap">
        {x.open && !arrived ? <Button variant={x.track.status === 'delivered' ? 'primary' : 'secondary'} icon="check" busy={busy} onClick={() => setStatus('arrived')}>It arrived</Button> : null}
        {x.open && !x.paid && arrived ? <Button variant="primary" icon="bag" onClick={() => checkout(false)}>Check out {formatCents(x.price_cents)}</Button> : null}
        {x.open && !x.paid && !arrived && x.price_cents > 0 ? <Button icon="bag" onClick={() => checkout(true)}>Take payment now {formatCents(x.price_cents)}</Button> : null}
        {x.open && x.paid && arrived ? <Button variant="primary" icon="check" busy={busy} onClick={() => setStatus('picked_up')}>Picked up</Button> : null}
        <OrderPrintButtons orderId={x.id} deviceLeft={x.device_left} />
        {x.open ? <Button variant="ghost" onClick={() => setCancel(true)}>Cancel order</Button> : null}
      </div>
      <div className="grid-2" style={{ alignItems: 'start' }}>
        <div className="stack">
          <section className="card stack-sm">
            <dl className="kv">
              <dt>Price</dt><dd className="strong"><Money cents={x.price_cents} />{x.taxable ? ' + tax' : ''}</dd>
              {x.cost_cents !== null && x.cost_cents !== undefined ? <><dt>Cost and profit</dt><dd><OrderCostLine x={x} onChange={o.setData} /></dd></> : null}
              <dt>Payment</dt>
              <dd>
                {x.paid ? <>Paid on <a href={`#/sales/${x.paid.sale_id}`}>receipt #{x.paid.receipt_no}</a>{x.paid.paid_at ? ` · ${formatDateTime(x.paid.paid_at)}` : ''}{x.paid.refunded ? ' · refunded' : ''}</>
                  : x.price_cents === 0 ? 'Nothing to pay' : x.pay_plan === 'now' ? 'Paying now (not paid yet)' : 'Pays at pickup'}
              </dd>
              <dt>{words.item[0].toUpperCase() + words.item.slice(1)}</dt>
              <dd>
                {x.device_left ? <>Left with us{x.device ? ` · ${x.device}` : ''}</> : 'Customer kept it'}
                {x.open ? <> · <button type="button" className="link-btn" onClick={() => setPhoneSheet(true)}>Change</button></> : null}
              </dd>
              <dt>Supplier</dt><dd>{x.supplier ?? '—'}{x.supplier_ref ? ` · order #${String(x.supplier_ref).replace(/^#/, '')}` : ''}</dd>
              {x.supplier_sku || x.part_url ? (
                <><dt>Part</dt><dd>{x.supplier_sku ? `SKU ${x.supplier_sku}` : ''}{x.part_url ? <>{x.supplier_sku ? ' · ' : ''}<a href={x.part_url} target="_blank" rel="noreferrer noopener">Open on {x.supplier ?? 'the'} site</a></> : null}</dd></>
              ) : null}
              {plan.home ? <><dt>Text when it arrives</dt><dd>{x.notify_sms ? 'Yes' : 'No'}</dd></> : null}
              {x.note ? <><dt>Note</dt><dd>{x.note}</dd></> : null}
            </dl>
          </section>
          <TrackingCard x={x} onChange={o.setData} />
        </div>
        <section className="card stack-sm">
          <h3>History</h3>
          {x.events.map((e: any) => (
            <div key={e.id} className="small"><span className="strong">{e.status ? ORDER_STATUS_LABELS[e.status as OrderStatus] : 'Update'}</span> · {formatDateTime(e.created_at)}{e.employee_name ? ` · ${e.employee_name}` : ''}{e.note ? <div className="muted">{e.note}</div> : null}</div>
          ))}
        </section>
      </div>
      {cancel ? (
        <Confirm title={`Cancel ${x.number}?`} confirmLabel="Cancel order" danger busy={busy} onClose={() => setCancel(false)} onConfirm={async () => { await setStatus('cancelled', reason); setCancel(false); }}>
          {x.paid && !x.paid.refunded ? <div className="banner warn"><Icon.alert /><div className="small">This order is paid. Refund receipt #{x.paid.receipt_no} first, then cancel it.</div></div> : null}
          <Field label="Reason"><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
        </Confirm>
      ) : null}
      {phoneSheet ? <PhoneSheet order={x} onClose={() => setPhoneSheet(false)} onSaved={(r) => { o.setData(r); setPhoneSheet(false); }} /> : null}
    </div>
  );
}
